import { useState, useEffect } from "react";
import { getStagingOrders } from "../../http/api";
import { isOrderForStagingBlotter } from "./useWebSocketUpdates";

// Helper function to ensure we never have more than the specified number of items
const limitToMaxItems = (array, maxItems) => {
  return array.length > maxItems ? array.slice(0, maxItems) : array;
};

export const useStagingOrders = (token, page = 0, size = 20, columnToSort = "updatedAt", sortingDirection = "desc", visitedTabs = new Set(), setVisitedTabs = () => {}, tabId = "0-0") => {
  const [stagingOrderData, setStagingOrderData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Skip API call if token is null (meaning this tab is not active)
    if (!token) return;

    // Check if we've already visited this tab and page combination
    // If we have, don't make the API call again
    const tabPageKey = `${tabId}-${page}-${size}`;
    if (visitedTabs.has(tabPageKey)) {
      return;
    }

    const fetchStagingOrders = async () => {
      setLoading(true);
      try {
        const response = await getStagingOrders(token, page, size, columnToSort, sortingDirection);
        // Filter to only include orders that belong in the Staging Blotter
        const filteredOrders = (response.content || []).filter(isOrderForStagingBlotter);
        setStagingOrderData(limitToMaxItems(filteredOrders, size));
        setTotalElements(response.totalElements || 0);
        
        // Mark this tab-page combination as visited by replacing any existing entries for this tab
        setVisitedTabs(prev => {
          const newSet = new Set(prev);
          // Remove any previous entries for this tab (regardless of page)
          Array.from(prev).forEach(key => {
            if (key.startsWith(`${tabId}-`)) {
              newSet.delete(key);
            }
          });
          // Add the current tab-page combination
          newSet.add(tabPageKey);
          return newSet;
        });
      } catch (error) {
        console.error("Error fetching staging orders:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStagingOrders();
  }, [token, page, size, columnToSort, sortingDirection, tabId, visitedTabs, setVisitedTabs]);

  return { stagingOrderData, setStagingOrderData, totalElements, setTotalElements, loading };
};
